import { cn } from 'lib/utils'
import { PRICING_DATA_THEME } from 'modules/billing/pricing-theme'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import React from 'react'

import { TakaLogo } from '../../../components/TakaLogo'

/** SVG sprite IDs declared by `<Sprite />` below — referenced via `<use href={...} />`. */
export const SPRITE_LOGO = '#taka-icn-logo'
export const SPRITE_CHECK = '#taka-icn-check'

interface OnboardingShellProps {
  /** Optional back handler. Renders a chevron-left button below the logo when provided. */
  onBack?: () => void
  /** Optional forward handler. Renders an arrow button in the top-right when provided. */
  onNext?: () => void
  children: React.ReactNode
}

/**
 * Dark canvas shell for the new onboarding. Same logo + grid + glow language as the login surface.
 *
 * Top-left: Taka mark. Optional back chevron sits below it. Children render
 * in a centered column.
 */
// `h-[100dvh]` (dynamic viewport height) tracks the iOS Safari keyboard so
// the MobileStickyFooter rides above it when an input is focused; `h-screen`
// (100vh) would leave the footer pinned under the keyboard.
export const OnboardingShell: React.FC<OnboardingShellProps> = ({ onBack, onNext, children }) => {
  return (
    <div
      className="taka-dark relative h-[100dvh] w-full overflow-x-clip overflow-y-auto bg-bg text-paper"
      data-theme={PRICING_DATA_THEME}
    >
      <Sprite />

      <div className="grid-bg pointer-events-none absolute inset-0 opacity-60" aria-hidden />
      <div
        className="glow"
        style={{
          top: '-260px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '900px',
          height: '600px',
        }}
        aria-hidden
      />

      {/* Mobile follows the iOS/Android navigation bar arrangement: back
          chevron sits to the left of the logo, and the logo shifts right
          to make room (`left-16` clears the chip's `top-5 left-5 h-8 w-8`
          footprint). Desktop keeps the chip in the gutter below the logo. */}
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Go back to the previous step"
          className={cn(
            'absolute top-5 left-5 z-20 inline-flex h-8 w-8 items-center justify-center rounded-[9px] border border-line-bright text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper',
            'lg:top-24 lg:left-10 lg:h-9 lg:w-auto lg:gap-1.5 lg:px-3 lg:text-[12px] lg:font-medium'
          )}
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2.2} />
          <span className="hidden lg:inline">Back</span>
        </button>
      )}

      <a
        href="/"
        aria-label="Taka"
        className={cn(
          'absolute top-6 z-20 flex items-center lg:top-8 lg:left-10',
          onBack ? 'left-16' : 'left-6'
        )}
      >
        <TakaLogo className="h-7 w-auto" />
      </a>

      {onNext && (
        <button
          type="button"
          onClick={onNext}
          aria-label="Continue to the next step"
          className="absolute top-5 right-5 z-20 inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-acid/40 bg-acid/10 px-3 text-[12px] font-medium text-acid transition-colors hover:bg-acid/20 lg:top-8 lg:right-10"
        >
          Continue
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
        </button>
      )}

      {/* Grid-stack: every direct child lands in the same `1fr` cell, which
          lets two phases (the exiting and the entering one during a phase
          handoff) overlap naturally without taking flex space side-by-side.
          `place-items-center` centers each child within the cell. The arrow
          combinator targets AnimatePresence's direct phase children so we
          don't have to wrap each one in a positioned div (which would gate
          opacity on shared `layoutId` children and kill the magic). */}
      <main className="relative z-10 mx-auto grid min-h-full w-full max-w-[1100px] grid-cols-1 grid-rows-[1fr] place-items-center px-5 py-24 sm:px-6 lg:py-16 [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:w-full">
        {children}
      </main>
    </div>
  )
}

const Sprite: React.FC = () => (
  <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
    <symbol id="taka-icn-logo" viewBox="0 0 42 42">
      <path
        fill="currentColor"
        d="M23.8926 35.0578C23.8926 35.5782 23.4707 36 22.9504 36H14.1901C13.6698 36 13.2479 35.5782 13.2479 35.0578V20.7584C13.2479 20.238 12.8261 19.8162 12.3058 19.8162H7.94218C7.42183 19.8162 7 19.3943 7 18.874V10.501C7 9.98065 7.42183 9.55882 7.94218 9.55882H11.6281V14.3152C11.6281 14.8355 12.0499 15.2574 12.5703 15.2574H16.9339C17.4542 15.2574 17.876 15.6792 17.876 16.1995V30.499C17.876 31.0193 18.2979 31.4412 18.8182 31.4412H23.8926V35.0578Z"
      />
      <path
        fill="currentColor"
        d="M35 18.874C35 19.3943 34.5782 19.8162 34.0578 19.8162H29.4628C28.9425 19.8162 28.5207 20.238 28.5207 20.7584V30.499C28.5207 31.0193 28.0988 31.4412 27.5785 31.4412H23.8926V16.1995C23.8926 15.6792 24.3144 15.2574 24.8347 15.2574H29.4297C29.9501 15.2574 30.3719 14.8355 30.3719 14.3152V10.501C30.3719 9.98065 29.9501 9.55882 29.4297 9.55882H11.6281V5.94218C11.6281 5.42183 12.0499 5 12.5703 5H34.0578C34.5782 5 35 5.42183 35 5.94218V18.874Z"
      />
    </symbol>
    <symbol id="taka-icn-check" viewBox="0 0 16 16">
      <polyline points="3.5 8.5 6.8 11.6 12.5 5" />
    </symbol>
  </svg>
)
